import { A11yProvider } from "@/context/A11yContext";
import { A11yPanel } from "@/components/A11yPanel";
import TermsTrainer from "@/pages/TermsTrainer";
import PracticePaper from "@/pages/PracticePaper";
import LyricsSheet from "@/pages/LyricsSheet";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { VisitTracker } from "@/components/VisitTracker";
import { PageMeta } from "@/components/PageMeta";
import AdminAnalytics from "@/pages/AdminAnalytics";
import Articles from "@/pages/Articles";
import LeaveReview from "@/pages/LeaveReview";
import LocalLessons from "@/pages/LocalLessons";
import { RefCapture } from "@/components/ReferralKit";
import { BackToTop } from "@/components/BackToTop";
import { FloatingTopArrow, GlobalNotes } from "@/components/FloatingExtras";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import Courses from "@/pages/Courses";
import InstrumentPage from "@/pages/InstrumentPage";
import CourseDetail from "@/pages/CourseDetail";
import LessonDetail from "@/pages/LessonDetail";
import Trainer from "@/pages/Trainer";
import RhythmClapPage from "@/pages/RhythmClapPage";
import ChoirPracticePage from "@/pages/ChoirPracticePage";
import WorksheetLibrary from "@/pages/WorksheetLibrary";
import MelodyEarTrainer from "@/pages/MelodyEarTrainer";
import HallOfFame from "@/pages/HallOfFame";
import Quizzes from "@/pages/Quizzes";
import Dashboard from "@/pages/Dashboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import About from "@/pages/About";
import JoinClass from "@/pages/JoinClass";
import AdminConsole from "@/pages/AdminConsole";
import AdminBacking from "@/pages/AdminBacking";
import AdminScores from "@/pages/AdminScores";
import AdminCutdowns from "@/pages/AdminCutdowns";
import AdminMore from "@/pages/AdminMore";
import AdminHelperReport from "@/pages/AdminHelperReport";
import AdminTerms from "@/pages/AdminTerms";
import AdminGalleryStories from "@/pages/AdminGalleryStories";
import AdminVideoWording from "@/pages/AdminVideoWording";
import AdminBankTransfers from "@/pages/AdminBankTransfers";
import AdminPayments from "@/pages/AdminPayments";
import MoreFromJason from "@/pages/MoreFromJason";
import ClassTeaching from "@/pages/ClassTeaching";
import Payment from "@/pages/Payment";
import NextLesson from "@/pages/NextLesson";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import LessonSearch from "@/pages/LessonSearch";
import Certificate from "@/pages/Certificate";
import GospelLicks from "@/pages/GospelLicks";
import ForgotPassword from "@/pages/ForgotPassword";
import PublicCertificate from "@/pages/PublicCertificate";
import PianoWaitlist from "@/pages/PianoWaitlist";
import WorshipServices from "@/pages/WorshipServices";
import DailyChallenge from "@/pages/DailyChallenge";
import Badges from "@/pages/Badges";
import Timeline from "@/pages/Timeline";
import WhyChoosePage from "@/pages/WhyChoose";
import Faqs from "@/pages/Faqs";
import Live from "@/pages/Live";
import Community from "@/pages/Community";
import KeepInTouch from "@/pages/KeepInTouch";
import { SiteFooter } from "@/components/SiteFooter";

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <A11yProvider>
        <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <VisitTracker />
          <PageMeta />
          <RefCapture />
          <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-full focus:bg-primary focus:px-5 focus:py-3 focus:text-primary-foreground" data-testid="skip-link">Skip to main content</a>
          <main id="main-content" tabIndex={-1} className="outline-none">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/more" element={<MoreFromJason />} />
            <Route path="/class-teaching" element={<ClassTeaching />} />
            <Route path="/join/:code" element={<JoinClass />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/search" element={<LessonSearch />} />
            <Route path="/waitlist" element={<PianoWaitlist />} />
            <Route path="/worship" element={<WorshipServices />} />
            <Route path="/instruments/:instrumentId" element={<InstrumentPage />} />
            <Route path="/courses/:instrumentId/:courseId" element={<CourseDetail />} />
            <Route path="/lessons" element={<Courses />} />
            <Route path="/lessons/:id" element={<LessonDetail />} />
            <Route path="/trainer" element={<Trainer />} />
            <Route path="/rhythm-clap" element={<RhythmClapPage />} />
            <Route path="/choir-practice" element={<ChoirPracticePage />} />
            <Route path="/worksheets" element={<WorksheetLibrary />} />
            <Route path="/ear-trainer" element={<MelodyEarTrainer />} />
            <Route path="/terms" element={<TermsTrainer />} />
            <Route path="/lyrics/:id" element={<LyricsSheet />} />
            <Route path="/practice-paper/:instrumentId/:courseId" element={<PracticePaper />} />
            <Route path="/hall-of-fame" element={<HallOfFame />} />
            <Route path="/daily-challenge" element={<DailyChallenge />} />
            <Route path="/badges" element={<Badges />} />
            <Route path="/timeline" element={<Timeline />} />
            <Route path="/why-choose" element={<WhyChoosePage />} />
            <Route path="/faqs" element={<Faqs />} />
            <Route path="/live" element={<Live />} />
            <Route path="/community" element={<Community />} />
            <Route path="/keep-in-touch" element={<KeepInTouch />} />
            <Route path="/quizzes" element={<Quizzes />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/register" element={<Register />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/next-lesson" element={<ProtectedRoute><NextLesson /></ProtectedRoute>} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/teacher"
              element={
                <ProtectedRoute>
                  <TeacherDashboard />
                </ProtectedRoute>
              }
            />
            <Route path="/admin/backing" element={<ProtectedRoute><AdminBacking /></ProtectedRoute>} />
            <Route path="/admin/scores" element={<ProtectedRoute><AdminScores /></ProtectedRoute>} />
            <Route path="/admin/cutdowns" element={<ProtectedRoute><AdminCutdowns /></ProtectedRoute>} />
            <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:slug" element={<Articles />} />
            <Route path="/review" element={<LeaveReview />} />
            <Route path="/piano-lessons-dartford" element={<LocalLessons />} />
            <Route path="/singing-lessons-dartford" element={<LocalLessons />} />
            <Route path="/music-theory-lessons-dartford" element={<LocalLessons />} />
            <Route path="/admin/more" element={<ProtectedRoute><AdminMore /></ProtectedRoute>} />
            <Route path="/admin/helper" element={<ProtectedRoute><AdminHelperReport /></ProtectedRoute>} />
            <Route path="/admin/terms" element={<ProtectedRoute><AdminTerms /></ProtectedRoute>} />
            <Route path="/admin/gallery-stories" element={<ProtectedRoute><AdminGalleryStories /></ProtectedRoute>} />
            <Route path="/admin/video-wording" element={<ProtectedRoute><AdminVideoWording /></ProtectedRoute>} />
            <Route path="/admin/bank-transfers" element={<ProtectedRoute><AdminBankTransfers /></ProtectedRoute>} />
            <Route path="/admin/payments" element={<ProtectedRoute><AdminPayments /></ProtectedRoute>} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminConsole />
                </ProtectedRoute>
              }
            />
            <Route path="/c/:certId" element={<PublicCertificate />} />
            <Route path="/gospel-licks" element={<GospelLicks />} />
            <Route
              path="/certificate/:instrumentId/:courseId"
              element={
                <ProtectedRoute>
                  <Certificate />
                </ProtectedRoute>
              }
            />
          </Routes>
          </main>
          <SiteFooter />
          <A11yPanel />
          <BackToTop />
          <FloatingTopArrow />
          <GlobalNotes />
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
        </A11yProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
